// ===================================================================
// SHELTER — Facility Library
// Pre-configured shelter facility templates that can be activated
// for any incident without re-entering details
// ===================================================================

import { useEffect, useState, useMemo } from 'react';
import {
  Title, Text, Paper, Group, Badge, Button, TextInput, Textarea,
  Select, Modal, Stack, SimpleGrid, Switch, NumberInput, MultiSelect,
  ActionIcon, Tooltip,
} from '@mantine/core';
import {
  IconPlus, IconSearch, IconBuildingCommunity, IconEdit, IconTrash,
  IconPlayerPlay, IconAccessible, IconPaw, IconRefresh,
} from '@tabler/icons-react';
import { notifications } from '@mantine/notifications';
import { useAuthStore } from '../../store/authStore';
import { useShelterStore } from '../../store/shelterStore';
import { useModulePermission } from '../../hooks/useModulePermission';
import { WORKER_URL } from '../../lib/api-switch';

const KV_KEY = 'shelter-facility-library';
const H_KV = { 'Content-Type': 'application/json', Origin: 'https://apps.opengov.com' };

interface FacilityTemplate {
  id: string;
  name: string;
  address: string;
  latitude: string;
  longitude: string;
  capacity: number;
  shelterType: string;
  isAda: boolean;
  petFriendly: boolean;
  facilities: string[]; // ["showers","kitchen","laundry","wifi","generator","medical"]
  contactName: string;
  contactPhone: string;
  contactEmail: string;
  operatingOrg: string;
  notes: string;
}

const SHELTER_TYPES = [
  { value: 'school', label: 'School' },
  { value: 'community_center', label: 'Community Center' },
  { value: 'church', label: 'Church / House of Worship' },
  { value: 'arena', label: 'Arena / Convention Center' },
  { value: 'government', label: 'Government Building' },
  { value: 'recreation', label: 'Recreation Center' },
  { value: 'hotel', label: 'Hotel / Motel' },
  { value: 'other', label: 'Other' },
];

const FACILITY_OPTIONS = [
  { value: 'showers', label: 'Showers' },
  { value: 'kitchen', label: 'Kitchen' },
  { value: 'laundry', label: 'Laundry' },
  { value: 'wifi', label: 'WiFi' },
  { value: 'generator', label: 'Generator' },
  { value: 'medical', label: 'Medical Area' },
  { value: 'parking', label: 'Parking Lot' },
  { value: 'playground', label: 'Playground' },
  { value: 'pet_area', label: 'Pet Area' },
  { value: 'loading_dock', label: 'Loading Dock' },
];

const emptyTemplate = (): Omit<FacilityTemplate, 'id'> => ({
  name: '', address: '', latitude: '', longitude: '', capacity: 100,
  shelterType: 'school', isAda: false, petFriendly: false, facilities: [],
  contactName: '', contactPhone: '', contactEmail: '', operatingOrg: '', notes: '',
});

export default function FacilityLibrary() {
  const { canEdit } = useModulePermission('shelter');
  const { activeIncident } = useAuthStore();
  const { createShelter, loadShelters, shelters } = useShelterStore();

  const [facilities, setFacilities] = useState<FacilityTemplate[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');

  // Modal
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState<FacilityTemplate | null>(null);
  const [form, setForm] = useState(emptyTemplate());
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${WORKER_URL}/api/config/${KV_KEY}`, { headers: H_KV });
      if (res.ok) {
        const data = await res.json();
        setFacilities(Array.isArray(data) ? data : []);
      }
    } catch { /* */ }
    setLoading(false);
  };

  useEffect(() => { load(); loadShelters(); }, []);

  const save = async (updated: FacilityTemplate[]) => {
    await fetch(`${WORKER_URL}/api/config/${KV_KEY}`, {
      method: 'PUT', headers: H_KV, body: JSON.stringify(updated),
    });
    setFacilities(updated);
  };

  const filtered = useMemo(() => {
    if (!search) return facilities;
    const q = search.toLowerCase();
    return facilities.filter(f =>
      f.name.toLowerCase().includes(q) || f.address.toLowerCase().includes(q) ||
      f.operatingOrg.toLowerCase().includes(q) || f.shelterType.toLowerCase().includes(q)
    );
  }, [facilities, search]);

  function openCreate() {
    setEditing(null);
    setForm(emptyTemplate());
    setModalOpen(true);
  }

  function openEdit(f: FacilityTemplate) {
    setEditing(f);
    setForm({ ...f });
    setModalOpen(true);
  }

  async function handleSave() {
    if (!form.name.trim()) {
      notifications.show({ message: 'Name is required', color: 'orange' });
      return;
    }
    setSaving(true);
    if (editing) {
      const updated = facilities.map(f => f.id === editing.id ? { ...form, id: editing.id } : f);
      await save(updated);
      notifications.show({ title: 'Updated', message: `${form.name} saved.`, color: 'green' });
    } else {
      const newFacility: FacilityTemplate = { ...form, id: `fac_${Date.now()}_${Math.random().toString(36).slice(2, 6)}` };
      await save([...facilities, newFacility]);
      notifications.show({ title: 'Created', message: `${form.name} added to library.`, color: 'green' });
    }
    setSaving(false);
    setModalOpen(false);
  }

  async function handleDelete(id: string) {
    if (!confirm('Remove this facility from the library?')) return;
    await save(facilities.filter(f => f.id !== id));
    notifications.show({ message: 'Facility removed.', color: 'gray' });
  }

  async function handleActivate(f: FacilityTemplate) {
    if (!activeIncident) {
      notifications.show({ title: 'No Incident', message: 'Select an active incident first.', color: 'orange' });
      return;
    }
    // Check if already activated for this incident
    const alreadyActive = shelters.some(s =>
      s.incidentId === String(activeIncident.id) && s.name === f.name && s.address === f.address
    );
    if (alreadyActive) {
      notifications.show({ title: 'Already Active', message: `${f.name} is already activated for this incident.`, color: 'yellow' });
      return;
    }
    try {
      await createShelter({
        incidentId: String(activeIncident.id),
        name: f.name,
        address: f.address,
        latitude: f.latitude,
        longitude: f.longitude,
        capacity: f.capacity,
        currentPopulation: 0,
        isAda: f.isAda,
        petFriendly: f.petFriendly,
        status: 'standby',
        shelterType: f.shelterType,
        facilities: JSON.stringify(f.facilities),
        contactName: f.contactName,
        contactPhone: f.contactPhone,
        contactEmail: f.contactEmail,
        operatingOrg: f.operatingOrg,
        notes: f.notes,
      });
      await loadShelters();
      notifications.show({ title: 'Activated', message: `${f.name} added to ${activeIncident.name} as standby.`, color: 'green' });
    } catch {
      notifications.show({ title: 'Error', message: 'Failed to activate shelter.', color: 'red' });
    }
  }

  const setField = <K extends keyof Omit<FacilityTemplate, 'id'>>(key: K, value: Omit<FacilityTemplate, 'id'>[K]) =>
    setForm(prev => ({ ...prev, [key]: value }));

  return (
    <Stack gap="md">
      <Group justify="space-between">
        <div>
          <Title order={3} c="black">Facility Library</Title>
          <Text size="sm" c="dimmed">Pre-configured shelter locations — activate for any incident</Text>
        </div>
        <Group gap="xs">
          <Button size="xs" variant="light" leftSection={<IconRefresh size={14} />} onClick={load}>Refresh</Button>
          {canEdit && (
            <Button size="xs" leftSection={<IconPlus size={14} />} onClick={openCreate}>Add Facility</Button>
          )}
        </Group>
      </Group>

      <TextInput
        placeholder="Search facilities..."
        leftSection={<IconSearch size={14} />}
        value={search}
        onChange={e => setSearch(e.currentTarget.value)}
        size="sm"
        style={{ maxWidth: 400 }}
      />

      {loading ? (
        <Text c="dimmed" ta="center" py="xl">Loading...</Text>
      ) : filtered.length === 0 ? (
        <Paper p="xl" withBorder ta="center">
          <IconBuildingCommunity size={40} color="gray" style={{ opacity: 0.3 }} />
          <Text c="dimmed" mt="sm">{search ? 'No facilities match your search.' : 'No facilities in the library yet.'}</Text>
          {canEdit && !search && <Button size="xs" variant="light" mt="sm" onClick={openCreate}>Add Your First Facility</Button>}
        </Paper>
      ) : (
        <SimpleGrid cols={{ base: 1, sm: 2, lg: 3 }}>
          {filtered.map(f => {
            const isActive = activeIncident && shelters.some(s =>
              s.incidentId === String(activeIncident.id) && s.name === f.name && s.address === f.address
            );
            return (
              <Paper key={f.id} p="md" withBorder style={{ borderLeft: `4px solid ${isActive ? '#16a34a' : '#e5e7eb'}` }}>
                <Group justify="space-between" mb="xs">
                  <Text fw={600} size="sm">{f.name}</Text>
                  <Group gap={4}>
                    {f.isAda && <Tooltip label="ADA Accessible"><IconAccessible size={14} color="#3b82f6" /></Tooltip>}
                    {f.petFriendly && <Tooltip label="Pet Friendly"><IconPaw size={14} color="#16a34a" /></Tooltip>}
                  </Group>
                </Group>
                <Text size="xs" c="dimmed" mb="xs">{f.address || 'No address'}</Text>
                <Group gap="xs" mb="xs">
                  <Badge size="xs" variant="light">{SHELTER_TYPES.find(t => t.value === f.shelterType)?.label || f.shelterType}</Badge>
                  <Badge size="xs" variant="light" color="blue">Capacity: {f.capacity}</Badge>
                </Group>
                {f.operatingOrg && <Text size="xs" c="dimmed">{f.operatingOrg}</Text>}
                {f.facilities.length > 0 && (
                  <Group gap={4} mt={4}>
                    {f.facilities.slice(0, 4).map(a => <Badge key={a} size="xs" variant="outline" color="gray">{a}</Badge>)}
                    {f.facilities.length > 4 && <Badge size="xs" variant="outline" color="gray">+{f.facilities.length - 4}</Badge>}
                  </Group>
                )}
                <Group justify="flex-end" mt="sm" gap="xs">
                  {canEdit && (
                    <>
                      <ActionIcon size="sm" variant="subtle" onClick={() => openEdit(f)}><IconEdit size={14} /></ActionIcon>
                      <ActionIcon size="sm" variant="subtle" color="red" onClick={() => handleDelete(f.id)}><IconTrash size={14} /></ActionIcon>
                    </>
                  )}
                  {isActive ? (
                    <Badge size="sm" color="green">Active</Badge>
                  ) : (
                    <Button size="compact-xs" variant="light" color="green" leftSection={<IconPlayerPlay size={12} />}
                      onClick={() => handleActivate(f)}
                      disabled={!activeIncident}>
                      Activate
                    </Button>
                  )}
                </Group>
              </Paper>
            );
          })}
        </SimpleGrid>
      )}

      {/* Create/Edit Modal */}
      <Modal opened={modalOpen} onClose={() => setModalOpen(false)} title={editing ? 'Edit Facility' : 'Add Facility'} size="lg">
        <Stack gap="sm">
          <SimpleGrid cols={2}>
            <TextInput label="Facility Name" required value={form.name} onChange={e => setField('name', e.currentTarget.value)} />
            <Select label="Type" data={SHELTER_TYPES} value={form.shelterType} onChange={v => setField('shelterType', v || 'other')} />
          </SimpleGrid>
          <TextInput label="Address" value={form.address} onChange={e => setField('address', e.currentTarget.value)} />
          <SimpleGrid cols={3}>
            <NumberInput label="Capacity" value={form.capacity} onChange={v => setField('capacity', Number(v) || 0)} min={0} />
            <TextInput label="Latitude" value={form.latitude} onChange={e => setField('latitude', e.currentTarget.value)} />
            <TextInput label="Longitude" value={form.longitude} onChange={e => setField('longitude', e.currentTarget.value)} />
          </SimpleGrid>
          <SimpleGrid cols={2}>
            <Switch label="ADA Accessible" checked={form.isAda} onChange={e => setField('isAda', e.currentTarget.checked)} />
            <Switch label="Pet Friendly" checked={form.petFriendly} onChange={e => setField('petFriendly', e.currentTarget.checked)} />
          </SimpleGrid>
          <MultiSelect label="Amenities" data={FACILITY_OPTIONS} value={form.facilities} onChange={v => setField('facilities', v)} searchable />
          <SimpleGrid cols={3}>
            <TextInput label="Contact Name" value={form.contactName} onChange={e => setField('contactName', e.currentTarget.value)} />
            <TextInput label="Contact Phone" value={form.contactPhone} onChange={e => setField('contactPhone', e.currentTarget.value)} />
            <TextInput label="Contact Email" value={form.contactEmail} onChange={e => setField('contactEmail', e.currentTarget.value)} />
          </SimpleGrid>
          <TextInput label="Operating Organization" value={form.operatingOrg} onChange={e => setField('operatingOrg', e.currentTarget.value)} placeholder="e.g., American Red Cross" />
          <Textarea label="Notes" value={form.notes} onChange={e => setField('notes', e.currentTarget.value)} autosize minRows={2} />
          <Group justify="flex-end" mt="sm">
            <Button variant="subtle" onClick={() => setModalOpen(false)}>Cancel</Button>
            <Button onClick={handleSave} loading={saving}>{editing ? 'Save Changes' : 'Add to Library'}</Button>
          </Group>
        </Stack>
      </Modal>
    </Stack>
  );
}
